import { useState, useEffect, useCallback, useMemo, useReducer } from 'react';
import useRequestApi from '../../api/requests/useRequestApi';
import useStagesApi from '../../api/stages/useStagesApi';
import useFieldsCommentsApi from '../../api/comments/useFieldsCommentsApi';
import useSubformApprovalsApi from '../../api/subformApprovals/useSubformApprovalsApi';
import { extractUniqueCommentsFromProgresses } from '../../utils/commentUtils';
import { getRequestOutcomeStatus } from '../../../utils/priorityBadgeUtils';
import { getCurrentUser } from '../../../services/authService';

const initialState = {
  forms: [],
  assignedFormIds: [],
  requestProgress: null,
  children: [],
  comments: {},
  // Nested structure: { [formId]: { [subformId]: approvalData } }
  subformApprovals: {},
  currentUserId: null,
  currentUserEmail: null,
  requestId: null,
  requestName: null,
  generatedId: null,
  loading: true,
  error: null,
  currentUserRoleId: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        forms: action.forms,
        assignedFormIds: action.assignedFormIds,
        requestProgress: action.requestProgress,
        comments: action.comments,
        subformApprovals: action.subformApprovals || {},
        currentUserId: action.currentUserId,
        currentUserEmail: action.currentUserEmail,
        requestId: action.requestId,
        requestName: action.requestName,
        generatedId: action.generatedId,
        currentUserRoleId: action.currentUserRoleId,
        requestDateEnded: action.requestDateEnded,
        requestOutcomeStatus: action.requestOutcomeStatus,
      };
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.error };
    case 'SET_CHILDREN':
      return { ...state, children: action.children };
    case 'UPDATE_COMMENTS':
      return {
        ...state,
        comments: {
          ...state.comments,
          [action.formId]: action.updater(state.comments[action.formId] || []),
        },
      };
    case 'ADD_SUBFORM_APPROVAL':
      return {
        ...state,
        subformApprovals: {
          ...state.subformApprovals,
          [action.formId]: {
            ...state.subformApprovals[action.formId],
            [action.subformId]: action.approval,
          },
        },
      };
    case 'REMOVE_SUBFORM_APPROVAL': {
      const formApprovals = { ...state.subformApprovals[action.formId] };
      delete formApprovals[action.subformId];
      return {
        ...state,
        subformApprovals: {
          ...state.subformApprovals,
          [action.formId]: formApprovals,
        },
      };
    }
    default:
      return state;
  }
};

/**
 * useFormReview - Feature hook for form review workflow
 *
 * Composes API hooks (requests, stages, comments, approvals) with UI state management.
 * Handles data fetching, tab navigation, comment management, and section approvals.
 *
 * @param {string|number} requestId - The request ID to review (from URL /admin/review/:id)
 */
const useFormReview = (requestId) => {
  const { getRequest, getSubmittedForms } = useRequestApi();
  const { getStageChildren } = useStagesApi();
  const { addComment } = useFieldsCommentsApi();
  const { approveSubform, unapproveSubform, getApprovalsByRequest } =
    useSubformApprovalsApi();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch request and forms data
  useEffect(() => {
    if (!requestId) return;

    const fetchData = async () => {
      dispatch({ type: 'FETCH_START' });

      try {
        // Fetch request, forms, and current user in parallel
        const [requestData, formsData, currentUser] = await Promise.all([
          getRequest(requestId),
          getSubmittedForms(requestId),
          getCurrentUser(),
        ]);

        const forms = formsData?.forms || [];

        // Collect ALL comments from ALL request progresses (like Development branch)
        const allUniqueComments = extractUniqueCommentsFromProgresses(
          requestData.requests_progresses
        );

        // Use all unique comments for all forms
        const comments = {};
        forms.forEach((form) => {
          comments[form.id] = allUniqueComments;
        });

        // Find the ACTIVE progress record (not just the most recent)
        const activeProgress =
          requestData.requests_progresses?.find(
            (rp) => rp.active === true || rp.active === 1
          ) ||
          requestData.requests_progresses?.slice().reverse()[0] ||
          null;

        // Fetch approvals using the active progress ID
        let subformApprovals = {};
        if (activeProgress?.id) {
          try {
            const approvalsData = await getApprovalsByRequest(requestId);
            // approvalMap is { [formId]: { [subformId]: approvalData } }
            subformApprovals = approvalsData.approvalMap || {};
          } catch (err) {
            console.error('Error fetching approvals:', err);
          }
        }

        dispatch({
          type: 'FETCH_SUCCESS',
          forms,
          assignedFormIds: formsData?.assignedFormIds || [],
          requestProgress: activeProgress,
          comments,
          subformApprovals,
          currentUserId: currentUser?.user?.id || null,
          currentUserEmail: currentUser?.user?.email || null,
          requestId: requestData?.id || null,
          requestName: requestData?.study_name || null,
          generatedId: requestData?.generated_id || null,
          currentUserRoleId: currentUser?.user?.Role?.id || null,
          requestDateEnded: requestData.date_ended,
          requestOutcomeStatus: getRequestOutcomeStatus(
            requestData.date_ended,
            requestData.requests_progresses
          ),
        });
      } catch (err) {
        dispatch({ type: 'FETCH_ERROR', error: err.message });
      }
    };

    fetchData();
  }, [requestId, getRequest, getSubmittedForms, getApprovalsByRequest]);

  // Fetch stage children when requestProgress changes
  useEffect(() => {
    const stageId = state.requestProgress?.StageRelation?.Child?.id;
    if (!stageId) return;

    getStageChildren(stageId)
      .then((data) =>
        dispatch({ type: 'SET_CHILDREN', children: data.children || [] })
      )
      .catch((err) => console.error('Error fetching stage children:', err));
  }, [state.requestProgress, getStageChildren]);

  // Derived state
  const activeForm = useMemo(
    () => (activeTab < state.forms.length ? state.forms[activeTab] : null),
    [state.forms, activeTab]
  );

  const activeFormComments = useMemo(
    () => (activeForm ? state.comments[activeForm.id] || [] : []),
    [activeForm, state.comments]
  );

  // Get approval status for a specific form's subforms (keyed by formId)
  const getFormApprovalStatus = useCallback(
    (formId) => {
      return state.subformApprovals[formId] || {};
    },
    [state.subformApprovals]
  );

  // Get comment status for a specific form's subforms (has any comments)
  const getFormCommentStatus = useCallback(
    (formId) => {
      const formComments = state.comments[formId] || [];
      const commentStatus = {};
      formComments.forEach((comment) => {
        if (comment.subForm_id) {
          commentStatus[comment.subForm_id] = true;
        }
      });
      return commentStatus;
    },
    [state.comments]
  );

  // Handlers
  const handleTabChange = useCallback((_, value) => {
    if (value >= 0) setActiveTab(value);
  }, []);

  /**
   * Check if request is assigned to current user (same classification as Approvals.js)
   * - "Assigned" (NEW): reviewer_id is null AND active is true
   * - "In Progress" (PENDING): reviewer_id === currentUserId AND active is true
   */
  const isRequestAssigned = useMemo(() => {
    const { requestProgress, currentUserRoleId } = state;
    if (!requestProgress?.active) return false;

    // Assigned (NEW): reviewer_id is null - user can claim it
    // In Progress (PENDING): reviewer_id matches current user
    return (
      requestProgress?.StageRelation?.Child?.approver_role_id ===
      currentUserRoleId
    );
  }, [state]);

  const canTakeAction = useCallback(
    (formId) => state.assignedFormIds.includes(formId),
    [state.assignedFormIds]
  );

  const handleAddComment = useCallback(
    async (comment, subFormId, attachedFiles) => {
      if (!activeForm?.filledForm?.id) return null;

      const newComment = await addComment({
        comment,
        subFormId,
        endUserFilledFormId: activeForm.filledForm.id,
        requestId,
        attachedFiles,
      });

      dispatch({
        type: 'UPDATE_COMMENTS',
        formId: activeForm.id,
        updater: (prev) => [...prev, newComment],
      });

      return newComment;
    },
    [activeForm, addComment, requestId]
  );

  const setCommentsForActiveForm = useCallback(
    (updater) => {
      if (!activeForm) return;
      dispatch({
        type: 'UPDATE_COMMENTS',
        formId: activeForm.id,
        updater: typeof updater === 'function' ? updater : () => updater,
      });
    },
    [activeForm]
  );

  // Section approval handlers
  const handleApproveSection = useCallback(
    async (subformId) => {
      if (
        !activeForm?.id ||
        !state.requestProgress?.id ||
        !state.requestProgress?.request_id
      ) {
        return null;
      }

      const approval = await approveSubform({
        subformId,
        formId: activeForm.id,
        requestId: state.requestProgress.request_id,
        requestProgressId: state.requestProgress.id,
      });

      dispatch({
        type: 'ADD_SUBFORM_APPROVAL',
        formId: activeForm.id,
        subformId,
        approval: {
          id: approval.id,
          approved: true,
          approver: approval.approver,
          approvedAt: approval.approved_at,
        },
      });

      return approval;
    },
    [activeForm, state.requestProgress, approveSubform]
  );

  const handleUndoApproval = useCallback(
    async (subformId) => {
      if (!activeForm?.id || !state.requestProgress?.id) return;

      const existingApproval =
        state.subformApprovals[activeForm.id]?.[subformId];

      await unapproveSubform({
        subformId,
        formId: activeForm.id,
        requestProgressId:
          existingApproval?.requestProgressId ?? state.requestProgress.id,
        requestId: state.requestProgress.request_id ?? state.requestId,
      });

      dispatch({
        type: 'REMOVE_SUBFORM_APPROVAL',
        formId: activeForm.id,
        subformId,
      });
    },
    [
      activeForm,
      state.requestProgress,
      state.requestId,
      state.subformApprovals,
      unapproveSubform,
    ]
  );

  // Get approval status for active form's subforms (keyed by formId)
  const activeFormApprovalStatus = useMemo(() => {
    if (!activeForm?.id) return {};
    return getFormApprovalStatus(activeForm.id);
  }, [activeForm, getFormApprovalStatus]);

  // Get comment status for active form's subforms
  const activeFormCommentStatus = useMemo(() => {
    if (!activeForm?.id) return {};
    return getFormCommentStatus(activeForm.id);
  }, [activeForm, getFormCommentStatus]);

  return {
    // Data
    submittedForms: state.forms,
    assignedFormIds: state.assignedFormIds,
    requestProgress: state.requestProgress,
    children: state.children,
    loading: state.loading,
    error: state.error,
    requestId: state.requestId,
    requestName: state.requestName,
    generatedId: state.generatedId,
    requestDateEnded: state.requestDateEnded,
    requestOutcomeStatus: state.requestOutcomeStatus,
    currentUserEmail: state.currentUserEmail,

    // Tabs
    activeTab,
    activeForm,
    handleTabChange,
    isFormTab: activeTab < state.forms.length,

    // Comments
    activeFormComments,
    setCommentsForActiveForm,
    handleAddComment,

    // Section Approvals
    activeFormApprovalStatus,
    activeFormCommentStatus,
    handleApproveSection,
    handleUndoApproval,
    getFormApprovalStatus,
    getFormCommentStatus,

    // Permissions
    canTakeActionOnForm: canTakeAction,
    isRequestAssigned,
  };
};

export default useFormReview;
